import { Alert } from '@/components/ui/alert';
import type { HiddenPayableSummary } from '@fluenceur/types';
import { FormatNumber } from '@chakra-ui/react';
import { InfoCircle } from 'iconoir-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface AffiliateHiddenDebtNoticeProps {
  /** What the brand owes affiliates it no longer counts as active, or null. */
  hiddenPayable: HiddenPayableSummary | null | undefined;
}

/**
 * Keeps a departed affiliate's debt in sight while the brand looks at its
 * active ones.
 *
 * The filter answers a useful question, "who can sell for me right now", and it
 * is left alone. What it must never do is let a brand forget what it owes to
 * someone who left, which is the one debt no other screen puts in front of them.
 *
 * The sentence states a fact and points nowhere on purpose. The figure is the
 * brand's whole inactive payable, not a description of the rows currently on
 * screen, so telling the brand to widen the filter would be a promise the table
 * cannot keep when a search is also narrowing it.
 */
const AffiliateHiddenDebtNotice: React.FC<AffiliateHiddenDebtNoticeProps> = ({ hiddenPayable }) => {
  const { t } = useTranslation();

  if (!hiddenPayable) return null;

  return (
    <Alert
      w="full"
      size="sm"
      status="info"
      icon={<InfoCircle />}
      title={
        <>
          {t('affiliates.table.filterNotice.hiddenDebt', {
            count: hiddenPayable.affiliateCount,
          })}{' '}
          <FormatNumber
            value={hiddenPayable.amount.amount}
            style="currency"
            currency={hiddenPayable.amount.currencyCode}
          />
        </>
      }
    />
  );
};

export default AffiliateHiddenDebtNotice;
